import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { acAPI } from "../../lib/academicApi";
import { PageHeader, Loading, Section, EmptyState, StatusPill } from "../../components/Common";
import { ArrowLeft, FileDown, Sparkles, ClipboardCheck, PlayCircle } from "lucide-react";
import { downloadAuthedPdf } from "../../lib/admApi";
import { toast } from "sonner";
import { useAuth } from "../../lib/auth";

/** Grade book — student roll-up + report card generation. */
export default function GradeBook() {
  const { school } = useParams();
  const { tenant } = useAuth();
  const nav = useNavigate();
  const [students, setStudents] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [gradebook, setGradebook] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [busy, setBusy] = useState(false);
  const [ai, setAi] = useState(null);
  const [term, setTerm] = useState(() => {
    // Send term unchanged so it matches gradebook rows written under
    // tenant.current_term (e.g. "Third Term"). No literal normalisation.
    return tenant?.current_term || "Third Term";
  });
  // Canonical AY resolved from AcademicCalendarService (via tenant payload).
  // No hard-coded literal fallback — the tenant payload always populates it.
  const academicYear = tenant?.academic_year || "2026";

  useEffect(() => {
    (async () => {
      const [st, su] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/education360/students?limit=2000`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("edu360.token")}` }
        }).then(r => r.json()).then(d => Array.isArray(d) ? d : (d.items || [])),
        acAPI.subjects().then(r => r.data),
      ]);
      setStudents(st || []);
      setSubjects(su || []);
      // Auto-select Edwin Thom if present, otherwise fall back to first row.
      const edwin = (st || []).find((s) => s.first_name === "Edwin" && s.last_name === "Thom");
      if (edwin) setSelectedId(edwin.id);
      else if (st?.length) setSelectedId(st[0].id);
    })();
  }, []);

  useEffect(() => {
    if (!selectedId) return;
    acAPI.gradebookStudent(selectedId, { academic_year: academicYear, term }).then(({ data }) => setGradebook(data));
  }, [selectedId, term, academicYear]);

  const student = useMemo(() => students.find(s => s.id === selectedId), [students, selectedId]);
  const subjectsById = useMemo(() => {
    const m = {};
    subjects.forEach(s => (m[s.id] = s));
    return m;
  }, [subjects]);

  const generateAI = async () => {
    if (!selectedId) return;
    setBusy(true);
    try {
      const { data } = await acAPI.aiStudent(selectedId);
      setAi(data);
      toast.success("Student summary generated");
    } catch { toast.error("AI unavailable"); }
    finally { setBusy(false); }
  };
  const dlReport = async () => {
    if (!selectedId) return;
    try { await downloadAuthedPdf(acAPI.reportCardPdf(selectedId, academicYear, term), `report-card.pdf`); }
    catch (e) { toast.error(e.message); }
  };
  const dlTranscript = async () => {
    if (!selectedId) return;
    try { await downloadAuthedPdf(acAPI.transcriptPdf(selectedId), `transcript.pdf`); }
    catch (e) { toast.error(e.message); }
  };

  // Only consider rows with a real weighted_total. Third-Term "Pending"
  // rows (weighted_total === null) MUST NOT coerce into 0%.
  const scoredRows = gradebook.filter(g => g.weighted_total !== null && g.weighted_total !== undefined);
  const pendingRows = gradebook.filter(g => g.weighted_total === null || g.weighted_total === undefined);
  const overall = scoredRows.length
    ? (scoredRows.reduce((a, b) => a + (b.weighted_total || 0), 0) / scoredRows.length).toFixed(1)
    : "—";
  const termHasPending = term === "Third Term" && pendingRows.length > 0;

  return (
    <div data-testid="gradebook">
      <PageHeader
        kicker="Academic360™ · Grade Book"
        title="Student academic profile"
        subtitle="Complete academic roll-up per student. Feeds report cards & transcripts."
        actions={
          <div className="flex gap-2">
            <button onClick={() => nav(`/education360/${school}/academic`)} className="hl-button-secondary text-xs"><ArrowLeft className="w-3 h-3 inline mr-1" />Hub</button>
            <button onClick={dlTranscript} disabled={!selectedId} className="hl-button-secondary text-xs disabled:opacity-50" data-testid="btn-transcript"><FileDown className="w-3 h-3 inline mr-1" />Transcript</button>
            <button onClick={dlReport} disabled={!selectedId} className="hl-button-primary text-xs disabled:opacity-50" data-testid="btn-report-card"><FileDown className="w-3 h-3 inline mr-1" />Report Card</button>
          </div>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <div className="lg:col-span-1">
          <Section title="Students">
            <div className="hl-card p-3 max-h-[540px] overflow-y-auto space-y-1" data-testid="student-list">
              {students.map(s => (
                <button
                  key={s.id}
                  onClick={() => setSelectedId(s.id)}
                  className={`w-full text-left px-3 py-1.5 rounded text-sm ${selectedId === s.id ? "bg-[color:var(--hl-navy)] text-white" : "hover:bg-slate-50 text-slate-700"}`}
                  data-testid={`student-${s.id}`}
                >
                  <div className="font-semibold">{s.first_name} {s.last_name}</div>
                  <div className={`text-xs ${selectedId === s.id ? "text-white/70" : "text-slate-500"}`}>{s.year_group}</div>
                </button>
              ))}
              {students.length === 0 && <div className="text-slate-500 text-sm">No students loaded.</div>}
            </div>
          </Section>
        </div>

        <div className="lg:col-span-3 space-y-6">
          {student ? (
            <>
              <div className="hl-card p-5 grid grid-cols-1 md:grid-cols-4 gap-4" data-testid="student-summary">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500">Student</div>
                  <div className="font-display font-bold text-xl text-[color:var(--hl-navy)]">{student.first_name} {student.last_name}</div>
                  <div className="text-xs text-slate-500">{student.admission_no} · {student.year_group}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500">Overall</div>
                  <div className="font-display font-bold text-3xl text-[color:var(--hl-orange)]">{overall === "—" ? <span className="text-slate-400 text-xl align-middle">N/A</span> : `${overall}%`}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500">Subjects</div>
                  <div className="font-display font-bold text-3xl">{gradebook.length || <span className="text-slate-400 text-xl align-middle">N/A</span>}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500">Term · {academicYear}</div>
                  <select value={term} onChange={e => setTerm(e.target.value)} className="mt-1 border border-slate-200 rounded px-2 py-1 text-sm" data-testid="term-picker">
                    {(tenant?.terms || ["First Term", "Second Term", "Third Term", "Autumn", "Spring", "Summer"]).map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
              </div>

              <Section
                title={`Subject grades · ${term}`}
                subtitle={termHasPending
                  ? "Third Term is in progress. Rows marked Pending await teacher mark entry — never rendered as 0%."
                  : undefined}
              >
                <div className="hl-card overflow-hidden" data-testid="grades-table">
                  <table className="hl-table">
                    <thead><tr><th>Subject</th><th>Continuous</th><th>Exam</th><th>Weighted</th><th>Grade</th><th>Rank</th></tr></thead>
                    <tbody>
                      {gradebook.length === 0 && (
                        <tr>
                          <td colSpan={6} className="text-center py-6 text-slate-500" data-testid="empty-gradebook">
                            {term === "Third Term"
                              ? "No assessments recorded for this student in Third Term yet."
                              : `Insufficient assessment data for ${term}.`}
                          </td>
                        </tr>
                      )}
                      {gradebook.map(g => {
                        const isPending = g.weighted_total === null || g.weighted_total === undefined;
                        return (
                          <tr key={g.id} data-testid={`gradebook-row-${g.subject_id}`}>
                            <td>{g.subject_name || subjectsById[g.subject_id]?.name || "—"}</td>
                            <td className="mono">{isPending ? <span className="text-slate-400">—</span> : `${g.continuous_avg}%`}</td>
                            <td className="mono">{isPending ? <span className="text-slate-400">—</span> : `${g.exam_avg}%`}</td>
                            <td className="mono font-semibold">
                              {isPending
                                ? <span className="text-amber-700">Pending</span>
                                : `${g.weighted_total}%`}
                            </td>
                            <td>
                              {isPending
                                ? <span className="text-xs uppercase tracking-widest text-amber-700">Pending</span>
                                : <span className={`grade-tag grade-${g.grade}`}>{g.grade}</span>}
                            </td>
                            <td className="mono">{g.rank_in_class || "—"}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </Section>

              <Section title="Executive Academic Assistant (advisory)"
                actions={
                  <button onClick={generateAI} disabled={busy} className="hl-button-primary text-xs disabled:opacity-50" data-testid="btn-ai-student">
                    <Sparkles className="w-3 h-3 inline mr-1" />{busy ? "Generating…" : "Generate student summary"}
                  </button>
                }
              >
                {ai ? (
                  <div className="hl-card p-5 border-l-4 border-[color:var(--hl-orange)]" data-testid="ai-summary">
                    <div className="text-[10px] uppercase tracking-widest text-[color:var(--hl-orange)]">{ai.kind} · Advisory</div>
                    <div className="font-display font-bold text-lg mt-1">{ai.title}</div>
                    <div className="text-sm whitespace-pre-wrap text-slate-700 mt-2">{ai.body}</div>
                  </div>
                ) : (
                  <EmptyState icon={Sparkles} title="No summary yet" body="Generate a student progress summary." testId="no-ai-student" />
                )}
              </Section>
            </>
          ) : (
            <EmptyState title="Select a student" body="Choose a student to view their academic profile." />
          )}
        </div>
      </div>
    </div>
  );
}
